import { Box, type SelectChangeEvent, Typography, ToggleButton } from '@mui/material'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { type lookup, type race } from 'core/types'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'

interface RaceSelectionProps {
  raceList: race[]
  isHispanic: boolean | null
  setRaceList: (raceList: race[], isHispanic: boolean | null) => void
  tribalAffiliationStatus?: boolean
  tribalAffiliationID?: number | null
  setTribeStatus?: (checked: boolean, tribeId: number | null) => void
  // defaults to the child wording; the caregiver edit page passes its own
  tribeTooltipText?: string
  // validation messages shown beneath the race grid and the Latino or Hispanic toggle respectively
  raceError?: string
  ethnicityError?: string
  required?: boolean
}

export function RaceSelection (props: RaceSelectionProps): JSX.Element {
  const [raceItems, setRaceItems] = useState<race[]>([])
  const [selectedRaceItems, setSelectedRaceItems] = useState<race[]>(props.raceList)
  const [isHispanic, setIsHispanic] = useState<boolean | null>(props.isHispanic)
  const [tribeItems, setTribeItems] = useState<lookup[]>([])
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, error } = await sendGet('/Race/GetLookup', {})
      if (error[0] === '') {
        const serverItems: race[] = response
        const raceList: race[] = []
        serverItems.forEach(r => {
          raceList.push(r)
        })
        setRaceItems(raceList)
      }
      if (props.tribalAffiliationStatus !== undefined) {
        const { response: response2, error: error2 } = await sendGet('/TribalAffiliation/GetLookup', {})
        if (error2[0] === '') {
          setTribeItems(response2)
        }
      }
    }
    void fetchData()
  }, [])

  useEffect(() => {
    props.setRaceList(selectedRaceItems, isHispanic)
  }, [selectedRaceItems, isHispanic])

  function RaceCheckBox (r: race): JSX.Element {
    const changeRace = (selected: boolean): void => {
      if (selected && !selectedRaceItems.map(rl => rl.name).includes(r.name)) {
        setSelectedRaceItems(([...selectedRaceItems, { id: r.id, name: r.name, codePosition: 0 }]))
      } else {
        setSelectedRaceItems(selectedRaceItems.filter(x => x.name !== r.name))
      }
    }
    const result = <CheckBoxWithLabel key={r.id} label={r.name} name={r.name} dataTestId={`raceCheckBox-${r.name}`} value={selectedRaceItems.map(rl => rl.name).includes(r.name)} setValue={changeRace} edge={'start'} />
    return result
  }

  const ethnicityChange = (val: any): void => {
    if (val != null) {
      setIsHispanic(Number(val) === 1)
    }
  }

  const changeTribalAffiliationStatus = (val: boolean): void => {
    if (props.setTribeStatus !== undefined) {
      props.setTribeStatus(val, null)
    }
  }

  const changeTribe = (event: SelectChangeEvent<number | ''>): void => {
    if (props.setTribeStatus !== undefined) {
      const x = Number(event.target.value)
      props.setTribeStatus(props.tribalAffiliationStatus ?? false, (props.tribalAffiliationStatus ?? false) ? x : null)
    }
  }

  return <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: 0 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }} gutterBottom>Race / Ethnicity{props.required && (<>*</>)}</Typography>
      {props.required !== true && props.raceError != null && props.raceError !== '' && <Box sx={{ fontWeight: 200 }}>Optional</Box>}
      {props.required === true && props.raceError != null && props.raceError !== '' && <Box sx={{ fontWeight: 400, color: '#E00000' }}>Required</Box>}
    </Box>
    <Box sx={{ backgroundColor: '#ECEDEF', borderRadius: '10px', p: '12px', display: 'flex', flexDirection: 'column', gap: '10px', border: props.raceError != null ? '1px solid' : undefined, borderColor: 'error.main' }}>
      {raceItems.filter(x => x.id !== 4).map(r => RaceCheckBox(r))}
      {props.tribalAffiliationStatus !== undefined && <CheckBoxWithLabel
        key={'tribalStatus'}
        label={'Tribal Affiliation'}
        name={'tribalAffiliationStatus'}
        dataTestId={'tribalAffiliationStatus'}
        value={props.tribalAffiliationStatus}
        setValue={changeTribalAffiliationStatus}
        edge={'start'}
      />}
    </Box>
    {props.raceError != null && (
      <Typography variant='caption' color='error' display='block' data-testid='raceError' sx={{ mt: '4px' }}>{props.raceError}</Typography>
    )}
    {props.tribalAffiliationStatus === true && (
      <SelectWithLabel
        name="tribalAffiliationID"
        width="100%"
        value={props.tribalAffiliationID ?? ''}
        options={tribeItems}
        onChange={changeTribe}
        FormControlProps={{ className: 'pb-0' }}
        // tribe names run long - wrap them (selected value and menu items) rather than widening the page on mobile
        SelectProps={{
          sx: { maxWidth: '100%', '& .MuiSelect-select': { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
          MenuProps: { sx: { '& .MuiMenuItem-root': { whiteSpace: 'normal', overflowWrap: 'anywhere' } } }
        }}
        labelContent={<CustomLabelWithToolTip
          name='childTribe'
          isdId={0}
          defaultLabelText='Tribal Affiliation'
          defaultTooltipText={props.tribeTooltipText ?? 'This is the tribal affiliation of the child'}
        />}
      />
    )}

    <Box display={'block'}>
      <Typography variant='subtitle1' gutterBottom>Latino or Hispanic</Typography>
      <ToggleButtonGroup className='yes-no-button-group' value={isHispanic === null ? null : (isHispanic ? 1 : 0)} onChange={(event, value) => { ethnicityChange(value) }} exclusive>
        <ToggleButton value={1} >
          Yes
        </ToggleButton>
        <ToggleButton value={0} >
          No
        </ToggleButton>
      </ToggleButtonGroup>
      {props.ethnicityError != null && (
        <Typography variant='caption' color='error' display='block' data-testid='ethnicityError' sx={{ mt: '4px' }}>{props.ethnicityError}</Typography>
      )}
    </Box>
  </>
}
